import { useContext, type CSSProperties } from "react";
import { Element, NodeContext, useEditor } from "@nkitaa/builder-engine";
import { motion } from "framer-motion";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import {
  type BorderSide,
  type RuntimeProps,
  ASSET_BASE,
  SECTION_REVEAL_PROPS,
  classNames,
  extractUtilityClasses,
  getBorderClasses,
  isVisible,
  parseHeroOppositeWidth,
  parseHeroWidth,
  sanitizeImageSrc,
  sectionMinHeight,
  toCssLength,
} from "./shared";
import { safeMediaSrc } from "../lib/safe-media-src";
import { TextChild } from "./textChild";
import { Button } from "./button";
import { type WBReveal, revealClass } from "./reveal";
import {
  overlayBackgroundStyle,
  type HeroOverlayStyle,
} from "./overlayStyle";

export function Hero(props: RuntimeProps) {
  const { currentColorPalette, currentRounding } = useWBTheme();
  const nodeId = useContext(NodeContext);
  // Above the guard. `useEditor` holds a `useRef`, so it occupies a slot in
  // React's hook list — calling it 139 lines below `return null` meant the first
  // render after a visibilityFilter hid this Hero ran one hook fewer and React
  // threw "Rendered fewer hooks than expected". A white screen on a live site.
  const appBarTransparent = useEditor((state) => {
    for (const node of Object.values(state.nodes)) {
      if (node?.data?.displayName === "AppBar") {
        return node.data.props?.transparent === true;
      }
    }
    return false;
  });

  if (!isVisible(props.isVisible as boolean | undefined)) return null;

  const buttonAlignment =
    (props.buttonAlignment as "flex-row" | "flex-col" | undefined) ??
    "flex-row";
  const textButtonAlignment =
    (props.textButtonAlignment as "flex-row" | "flex-col" | undefined) ??
    "flex-col";
  const padding =
    (props.padding as string | undefined) ?? "p-4 wb-md:p-6 wb-lg:p-8";
  const sectionMargin = (props.sectionMargin as string | undefined) ?? "py-0";
  const distHeadlineHeader =
    (props.distHeadlineHeader as string | undefined) ?? "mb-2";
  const distHeaderText = (props.distHeaderText as string | undefined) ?? "mb-2";
  const distTextButtons =
    (props.distTextButtons as string | undefined) ?? "gap-3";
  const distButtons = (props.distButtons as string | undefined) ?? "gap-3";
  const distContentImage =
    (props.distContentImage as string | undefined) ?? "gap-3";
  const inversed = (props.inversed as boolean | undefined) ?? false;
  const layoutWidth =
    (props.layoutWidth as "100%" | "75%" | "50%" | "33%" | "25%" | undefined) ??
    "100%";
  const borderRadius = props.borderRadius as string | undefined;
  const border = (props.border as boolean | undefined) ?? false;
  const borderColor = props.borderColor as string | undefined;
  const borderWidth = (props.borderWidth as string | undefined) ?? "border-2";
  const borderStyle =
    (props.borderStyle as string | undefined) ?? "border-solid";
  const borderSides = (props.borderSides as BorderSide[] | undefined) ?? [];
  const isVisibleHeadline =
    (props.isVisibleHeadline as boolean | undefined) ?? true;
  const textHeadline = props.textHeadline as string | undefined;
  const fontSizeHeadline =
    (props.fontSizeHeadline as string | undefined) ?? "text-lg";
  const textAlignHeadline =
    (props.textAlignHeadline as
      | "left"
      | "center"
      | "right"
      | "justify"
      | undefined) ?? "center";
  const isVisibleHeader =
    (props.isVisibleHeader as boolean | undefined) ?? true;
  const textHeader = props.textHeader as string | undefined;
  const fontSizeHeader =
    (props.fontSizeHeader as string | undefined) ?? "text-xl";
  const textAlignHeader =
    (props.textAlignHeader as
      | "left"
      | "center"
      | "right"
      | "justify"
      | undefined) ?? "center";
  const isVisibleText = (props.isVisibleText as boolean | undefined) ?? true;
  const textText = props.textText as string | undefined;
  const fontSizeText = (props.fontSizeText as string | undefined) ?? "text-sm";
  const textAlignText =
    (props.textAlignText as
      | "left"
      | "center"
      | "right"
      | "justify"
      | undefined) ?? "center";
  const secondaryButtonSize =
    (props.secondaryButtonSize as
      | "xlarge"
      | "large"
      | "medium"
      | "small"
      | "xsmall"
      | undefined) ?? "small";
  const isVisibleSecondaryButton =
    (props.isVisibleSecondaryButton as boolean | undefined) ?? true;
  const secondaryButtonBorder =
    (props.secondaryButtonBorder as boolean | undefined) ?? true;
  const secondaryButtonBorderColor = props.secondaryButtonBorderColor as
    | string
    | undefined;
  const secondaryButtonBackgroundColor =
    props.secondaryButtonBackgroundColor as string | undefined;
  const secondaryButtonUnderline =
    (props.secondaryButtonUnderline as boolean | undefined) ?? false;
  const secondaryButtonFontColor = props.secondaryButtonFontColor as
    | string
    | undefined;
  const secondaryButtonText = props.secondaryButtonText as string | undefined;
  const primaryButtonSize =
    (props.primaryButtonSize as
      | "xlarge"
      | "large"
      | "medium"
      | "small"
      | "xsmall"
      | undefined) ?? "small";
  const primaryButtonFontColor = props.primaryButtonFontColor as
    | string
    | undefined;
  const primaryButtonBackgroundColor = props.primaryButtonBackgroundColor as
    | string
    | undefined;
  const primaryButtonBorder =
    (props.primaryButtonBorder as boolean | undefined) ?? true;
  const primaryButtonBorderColor = props.primaryButtonBorderColor as
    | string
    | undefined;
  const primaryButtonText = props.primaryButtonText as string | undefined;
  const isVisiblePrimaryButton =
    (props.isVisiblePrimaryButton as boolean | undefined) ?? true;
  const primaryButtonUnderline =
    (props.primaryButtonUnderline as boolean | undefined) ?? false;
  const isVisibleImage = (props.isVisibleImage as boolean | undefined) ?? true;
  const imageLayout =
    (props.imageLayout as
      | "background"
      | "left"
      | "right"
      | "none"
      | undefined) ?? "background";
  const backgroundOverlayColor =
    (props.backgroundOverlayColor as string | undefined) ?? "#00000080";
  const overlayStyle = props.overlayStyle as HeroOverlayStyle | undefined;
  const objectPosition = props.objectPosition as string | undefined;
  const reveal = props.reveal as WBReveal | undefined;
  const backgroundColor =
    (props.backgroundColor as string | undefined) ?? "#f5f5f5";
  const imageSrc = (props.imageSrc as string | undefined) ?? `${ASSET_BASE}/shared/hero.jpg`;
  const imageRounded = props.imageRounded as string | undefined;
  const imageAlt = (props.imageAlt as string | undefined) ?? "";
  const sectionHeight = (props.sectionHeight as number | undefined) ?? 60;
  const contentAlignment =
    (props.contentAlignment as "top" | "center" | "bottom" | undefined) ??
    "center";
  const backgroundType =
    (props.backgroundType as "image" | "video" | undefined) ?? "image";
  const videoSrc = props.videoSrc as string | undefined;
  const videoPosterSrc = props.videoPosterSrc as string | undefined;
  const videoLoop = (props.videoLoop as boolean | undefined) ?? true;

  const alignmentClass =
    contentAlignment === "center"
      ? "justify-center"
      : contentAlignment === "bottom"
        ? "justify-end"
        : "justify-start";
  const backgroundImageUrl = sanitizeImageSrc(imageSrc);
  const backgroundStyle: CSSProperties =
    isVisibleImage && backgroundType === "image" && imageLayout === "background" && backgroundImageUrl
      ? {
          backgroundImage: `url(${backgroundImageUrl})`,
          backgroundSize: "cover",
          backgroundPosition: objectPosition ?? "center",
        }
      : { backgroundColor };
  const roundedClasses = extractUtilityClasses(
    borderRadius ?? currentRounding,
    "rounded",
  );
  const borderClasses = getBorderClasses(
    border,
    borderWidth,
    borderStyle,
    borderSides,
  );
  const imageRoundedClasses = extractUtilityClasses(
    imageRounded ?? currentRounding,
    "rounded",
  );
  const leftWidth = parseHeroWidth(layoutWidth);
  const rightWidth = parseHeroOppositeWidth(layoutWidth);
  // Single source of truth for the content/image flex direction — kept in sync
  // with the editor (main-frontend Hero.tsx). Mobile is always a column
  // (inversed flips the stacking order); the desktop row only applies when
  // there is an actual side image (imageLayout left/right) AND a non-100%
  // split, and then imageLayout alone decides the side. Avoids the previous bug
  // where two independent ternaries emitted BOTH wb-md:flex-row and
  // wb-md:flex-row-reverse (non-deterministic).
  const hasSideImage =
    isVisibleImage &&
    backgroundType !== "video" &&
    (imageLayout === "left" || imageLayout === "right");
  const sideBySide = hasSideImage && layoutWidth !== "100%";
  const heroFlexDirection = classNames(
    inversed ? "flex-col-reverse" : "flex-col",
    sideBySide
      ? imageLayout === "right"
        ? "wb-md:flex-row-reverse"
        : "wb-md:flex-row"
      : undefined,
  );
  const contentClass = classNames(
    "relative items-center z-10 flex w-full",
    heroFlexDirection,
    distContentImage,
  );

  return (
    <section
      id={nodeId || undefined}
      className={classNames(
        "websitebuilder w-full flex justify-center scroll-mt-24",
        sectionMargin,
      )}
    >
      <div
        className={classNames(
          "relative w-full flex flex-col items-center overflow-hidden",
          padding,
          alignmentClass,
          borderClasses,
          roundedClasses,
          revealClass(reveal),
        )}
        style={{
          ...backgroundStyle,
          minHeight: sectionMinHeight(sectionHeight, appBarTransparent),
          borderColor: borderColor ?? currentColorPalette.secondary,
          border: border ? undefined : "none",
          borderRadius: roundedClasses
            ? undefined
            : toCssLength(borderRadius ?? currentRounding),
        }}
      >
        {isVisibleImage && backgroundType === "video" && videoSrc ? (
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src={safeMediaSrc(videoSrc)}
            poster={safeMediaSrc(videoPosterSrc)}
            autoPlay
            loop={videoLoop}
            muted
            playsInline
            disablePictureInPicture
          />
        ) : null}
        {isVisibleImage && (imageLayout === "background" || backgroundType === "video") ? (
          <div
            className="absolute inset-0"
            style={overlayBackgroundStyle(backgroundOverlayColor, overlayStyle)}
          />
        ) : null}
        <motion.div {...SECTION_REVEAL_PROPS} className={contentClass}>
          {isVisibleImage && backgroundType !== "video" &&
          (imageLayout === "right" || imageLayout === "left") ? (
            <div className={leftWidth}>
              <img
                src={backgroundImageUrl}
                alt={imageAlt}
                className={classNames(
                  "object-cover w-full",
                  imageRoundedClasses,
                )}
                style={{
                  borderRadius: imageRoundedClasses
                    ? undefined
                    : toCssLength(imageRounded ?? currentRounding),
                }}
              />
            </div>
          ) : null}
          <div
            className={classNames(
              rightWidth,
              "flex justify-center items-center w-full",
              textButtonAlignment,
              distTextButtons,
            )}
          >
            <div className="w-full">
              <div className={distHeadlineHeader}>
                <Element
                  is={TextChild}
                  id="headline"
                  isVisible={isVisibleHeadline}
                  text={textHeadline}
                  fontSize={fontSizeHeadline}
                  header
                  headingLevel="h2"
                  textAlign={textAlignHeadline}
                />
              </div>
              <div className={distHeaderText}>
                <Element
                  is={TextChild}
                  id="header"
                  isVisible={isVisibleHeader}
                  text={textHeader}
                  fontSize={fontSizeHeader}
                  header
                  headingLevel="h1"
                  textAlign={textAlignHeader}
                />
              </div>
              <Element
                is={TextChild}
                id="text"
                isVisible={isVisibleText}
                text={textText}
                fontSize={fontSizeText}
                textAlign={textAlignText}
              />
            </div>
            <div
              className={classNames(
                distButtons,
                "flex justify-center items-center",
                buttonAlignment,
              )}
            >
              <Element
                is={Button}
                id="buttonOne"
                text={secondaryButtonText}
                size={secondaryButtonSize}
                border={secondaryButtonBorder}
                type="secondary"
                backgroundColor={secondaryButtonBackgroundColor}
                textColor={secondaryButtonFontColor}
                borderColor={secondaryButtonBorderColor}
                underline={secondaryButtonUnderline}
                isVisible={isVisibleSecondaryButton}
              />
              <Element
                is={Button}
                id="buttonTwo"
                text={primaryButtonText}
                border={primaryButtonBorder}
                size={primaryButtonSize}
                backgroundColor={primaryButtonBackgroundColor}
                textColor={primaryButtonFontColor}
                borderColor={primaryButtonBorderColor}
                underline={primaryButtonUnderline}
                isVisible={isVisiblePrimaryButton}
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
